import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import {
  UK, LEASE_FILTERS, fmt, leaseAccent, expiryBadge, depositBadge,
  isLeaseEnded, isLeaseActive, readyToRenew, s13StatusLabel,
} from './leaseFormShared';

const PAGE_SIZE = 15;

export default function Leases() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [leases, setLeases] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [bankDetailsReady, setBankDetailsReady] = useState(false);

  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [emailingId, setEmailingId] = useState<number | null>(null);
  const [s13LeaseId, setS13LeaseId] = useState<number | null>(null);
  const [s13Form, setS13Form] = useState({ proposed_rent: '', effective_date: '', notice_date: '', notes: '' });
  const [rentIncreases, setRentIncreases] = useState<any[]>([]);
  const [savingS13, setSavingS13] = useState(false);
  const [servingId, setServingId] = useState<number | null>(null);
  const [applyingId, setApplyingId] = useState<number | null>(null);

  const [endLeaseId, setEndLeaseId] = useState<number | null>(null);
  const [endForm, setEndForm] = useState({ move_out_date: '', reason: '', early_termination_fee: '', deposit_returned: '', deposit_deductions: '', notes: '' });
  const [ending, setEnding] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const r = await fetch(`${API_URL}/landlord/leases`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setLeases(d.leases || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, filterTab]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/business`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setBankDetailsReady(!!d.bankDetailsReady))
      .catch(() => setBankDetailsReady(false));
  }, [token]);

  useEffect(() => {
    const endId = searchParams.get('end');
    if (endId && leases.length) {
      const l = leases.find((x: any) => String(x.id) === endId);
      if (l) openEnd(l);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, leases, setSearchParams]);

  const activeLeases = leases.filter(isLeaseActive);
  const expiring = leases.filter(l => isLeaseActive(l) && Number(l.days_to_expiry) <= 60);
  const expired = leases.filter(l => {
    const s = String(l.lease_status || '').toLowerCase();
    return s !== 'ended' && !l.ended_at && s !== 'notice_given' && Number(l.days_to_expiry) < 0;
  });
  const renewCount = leases.filter(readyToRenew).length;

  const filtered = leases.filter((l: any) => {
    const status = String(l.lease_status || '').toLowerCase();
    const ended = isLeaseEnded(l);
    const notice = status === 'notice_given';
    if (filterTab === 'Active') { if (!isLeaseActive(l)) return false; }
    if (filterTab === 'Notice') { if (!notice) return false; }
    if (filterTab === 'Expiring') { if (!isLeaseActive(l) || Number(l.days_to_expiry) > 60) return false; }
    if (filterTab === 'Expired') {
      if (ended || notice || Number(l.days_to_expiry) >= 0) return false;
    }
    if (filterTab === 'Ended') { if (!ended) return false; }
    if (filterTab === 'Ready to Renew') { if (!readyToRenew(l)) return false; }
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (l.property_address || '').toLowerCase().includes(q)
      || (l.tenant_name || '').toLowerCase().includes(q)
      || (l.room_number || '').toLowerCase().includes(q)
      || (l.tenancy_type || '').toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterCount = (f: string) => {
    if (f === 'Active') return activeLeases.length;
    if (f === 'Notice') return leases.filter(l => String(l.lease_status || '').toLowerCase() === 'notice_given').length;
    if (f === 'Expiring') return expiring.length;
    if (f === 'Expired') return expired.length;
    if (f === 'Ended') return leases.filter(isLeaseEnded).length;
    if (f === 'Ready to Renew') return renewCount;
    return 0;
  };

  const generatePdf = async (l: any) => {
    setGeneratingId(l.id);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${l.id}/agreement`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const d = await r.json(); showToast(d.error || 'Failed'); setGeneratingId(null); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenancy-${l.property_address.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF saved — also in tenant portal');
    } catch { showToast('Failed to generate PDF'); }
    setGeneratingId(null);
  };

  const emailAgreement = async (l: any) => {
    if (!window.confirm(`Email tenancy agreement for ${l.tenant_name}?`)) return;
    setEmailingId(l.id);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${l.id}/agreement/email`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      showToast(r.ok ? `Sent to ${d.sent_to}` : d.error || 'Failed');
    } catch { showToast('Network error'); }
    setEmailingId(null);
  };

  const openS13 = async (l: any) => {
    if (s13LeaseId === l.id) { setS13LeaseId(null); return; }
    setS13LeaseId(l.id);
    const eff = new Date();
    eff.setMonth(eff.getMonth() + 2);
    const notice = new Date(eff);
    notice.setMonth(notice.getMonth() - 1);
    setS13Form({
      proposed_rent: String(Math.round(Number(l.monthly_rent || 0) * 1.05)),
      effective_date: eff.toISOString().split('T')[0],
      notice_date: notice.toISOString().split('T')[0],
      notes: '',
    });
    const r = await fetch(`${API_URL}/landlord/rent-increases?lease_id=${l.id}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setRentIncreases(d.increases || []);
  };

  const saveS13 = async (l: any) => {
    setSavingS13(true);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lease_id: l.id, ...s13Form, proposed_rent: Number(s13Form.proposed_rent) }),
      });
      const d = await r.json();
      if (!r.ok) showToast(d.error || 'Failed');
      else {
        showToast('Section 13 drafted');
        const ir = await fetch(`${API_URL}/landlord/rent-increases?lease_id=${l.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setRentIncreases((await ir.json()).increases || []);
      }
    } catch { showToast('Network error'); }
    setSavingS13(false);
  };

  const serveNotice = async (incId: number, l: any) => {
    if (!window.confirm('Email Section 13 notice to tenant?')) return;
    setServingId(incId);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases/${incId}/serve-notice`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      showToast(r.ok ? 'Notice emailed' : (await r.json()).error || 'Failed');
      if (r.ok) {
        const ir = await fetch(`${API_URL}/landlord/rent-increases?lease_id=${l.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setRentIncreases((await ir.json()).increases || []);
      }
    } catch { showToast('Network error'); }
    setServingId(null);
  };

  const applyIncrease = async (incId: number, l: any) => {
    if (!window.confirm('Apply rent increase now?')) return;
    setApplyingId(incId);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases/${incId}/apply`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      showToast(r.ok ? 'Rent applied' : (await r.json()).error || 'Failed');
      if (r.ok) { load(); openS13(l); }
    } catch { showToast('Network error'); }
    setApplyingId(null);
  };

  const openEnd = (l: any) => {
    setEndLeaseId(l.id);
    setEndForm({
      move_out_date: new Date().toISOString().split('T')[0],
      reason: '', early_termination_fee: '', deposit_returned: String(l.deposit || ''),
      deposit_deductions: '', notes: '',
    });
  };

  const confirmEnd = async (l: any) => {
    setEnding(true);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${l.id}/end-tenancy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          end_date: endForm.move_out_date,
          reason: endForm.reason || null,
          early_termination_fee: endForm.early_termination_fee ? Number(endForm.early_termination_fee) : null,
          deposit_returned: endForm.deposit_returned ? Number(endForm.deposit_returned) : null,
          deposit_deductions: endForm.deposit_deductions || null,
          notes: endForm.notes || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) showToast(d.error || 'Failed');
      else {
        showToast(d.status_transition === 'notice_given' ? 'Notice recorded' : 'Tenancy ended');
        setEndLeaseId(null);
        load();
      }
    } catch { showToast('Network error'); }
    setEnding(false);
  };

  const s13Lease = s13LeaseId ? leases.find(l => l.id === s13LeaseId) : null;
  const endLease = endLeaseId ? leases.find(l => l.id === endLeaseId) : null;

  const CSS = `
    .ls-page { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: 'DM Sans', system-ui, sans-serif; background: #eef2f6; min-height: 100vh; }
    .ls-banner-wrap { margin: -24px -24px 28px; padding: 10px 10px 14px; background: linear-gradient(145deg, #064e3b 0%, #0d9488 38%, #0369a1 72%, #1e40af 100%); border-radius: 0 0 22px 22px; box-shadow: 0 12px 40px rgba(1,33,105,0.22); }
    .ls-banner-glass { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; background: rgba(255,255,255,0.11); backdrop-filter: blur(22px); border: 1px solid rgba(255,255,255,0.28); border-radius: 18px; padding: 34px 38px; }
    .ls-banner-glass h1 { margin: 0; font-size: 34px; font-weight: 800; color: #fff; }
    .ls-banner-glass p { margin: 8px 0 0; font-size: 14px; color: rgba(255,255,255,0.88); }
    .ls-btn-add { background: #fff; color: ${UK.blue}; border: none; border-radius: 10px; padding: 11px 24px; font-weight: 800; font-size: 14px; cursor: pointer; }
    .ls-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .ls-kpi { border-radius: 16px; padding: 26px 24px 22px; min-height: 118px; }
    .ls-kpi-white { background: ${UK.white}; border: 1.5px solid #e2e8f0; }
    .ls-kpi-blue { background: linear-gradient(145deg, ${UK.blue}, #1e3a8a); }
    .ls-kpi-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); }
    .ls-kpi-red { background: linear-gradient(145deg, ${UK.red}, #ef4444); }
    .ls-kpi .kl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; margin-bottom: 8px; }
    .ls-kpi-white .kl { color: #64748b; } .ls-kpi-blue .kl, .ls-kpi-gold .kl, .ls-kpi-red .kl { color: rgba(255,255,255,0.85); }
    .ls-kpi .kv { font-size: 32px; font-weight: 800; font-family: 'DM Mono', monospace; }
    .ls-kpi-white .kv { color: ${UK.blue}; } .ls-kpi-blue .kv, .ls-kpi-gold .kv, .ls-kpi-red .kv { color: #fff; }
    .ls-kpi .ks { font-size: 12px; margin-top: 6px; }
    .ls-kpi-white .ks { color: #94a3b8; } .ls-kpi-blue .ks, .ls-kpi-gold .ks, .ls-kpi-red .ks { color: rgba(255,255,255,0.75); }
    .ls-toolbar { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
    .ls-search { flex: 1; min-width: 220px; max-width: 420px; padding: 10px 16px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; }
    .ls-search input { border: none; outline: none; width: 100%; font-size: 14px; }
    .ls-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: ${UK.blue}; min-width: 200px; cursor: pointer; }
    .ls-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
    .ls-card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.04); cursor: pointer; }
    .ls-accent { height: 4px; }
    .ls-card-body { padding: 16px 18px 12px; }
    .ls-card-foot { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f1f5f9; }
    .ls-act { padding: 8px 6px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; border: none; text-align: center; }
    .ls-act-white { background: #fff; color: ${UK.blue}; border: 1.5px solid ${UK.blue}; }
    .ls-act-blue { background: ${UK.blue}; color: #fff; }
    .ls-act-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); color: #fff; }
    .ls-act-green { background: #16a34a; color: #fff; }
    .ls-act-red { background: ${UK.red}; color: #fff; }
    .ls-pager { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 20px; }
    .ls-pager button { padding: 8px 16px; border-radius: 8px; border: 1px solid #d1d5db; background: #fff; font-weight: 700; cursor: pointer; }
    .ls-pager button:disabled { opacity: 0.4; cursor: default; }
    @media (max-width: 900px) { .ls-kpi-row { grid-template-columns: repeat(2, 1fr); } }
  `;

  return (
    <div className="ls-page">
      <style>{CSS}</style>

      <div className="ls-banner-wrap">
        <div className="ls-banner-glass">
          <div>
            <h1>Leases</h1>
            <p>{leases.length} tenanc{leases.length !== 1 ? 'ies' : 'y'} · {activeLeases.length} active · £{activeLeases.reduce((s, l) => s + Number(l.monthly_rent || 0), 0).toLocaleString()}/mo rent roll</p>
          </div>
          <button type="button" className="ls-btn-add" onClick={() => navigate('/landlord/leases/new')}>+ New Tenancy</button>
        </div>
      </div>

      <div className="ls-kpi-row">
        <div className="ls-kpi ls-kpi-white">
          <div className="kl">Total</div>
          <div className="kv">{leases.length}</div>
          <div className="ks">all tenancies</div>
        </div>
        <div className="ls-kpi ls-kpi-blue">
          <div className="kl">Active</div>
          <div className="kv">{activeLeases.length}</div>
          <div className="ks">current agreements</div>
        </div>
        <div className="ls-kpi ls-kpi-gold">
          <div className="kl">Expiring ≤60d</div>
          <div className="kv">{expiring.length}</div>
          <div className="ks">{renewCount > 0 ? `${renewCount} ready to renew` : 'renewals due'}</div>
        </div>
        <div className="ls-kpi ls-kpi-red">
          <div className="kl">Expired</div>
          <div className="kv">{expired.length}</div>
          <div className="ks">{expired.length ? 'needs action' : 'none'}</div>
        </div>
      </div>

      {renewCount > 0 && filterTab !== 'Ready to Renew' && (
        <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontWeight: 700, color: '#5b21b6', fontSize: 13 }}>{renewCount} ended tenanc{renewCount !== 1 ? 'ies' : 'y'} ready to renew</span>
          <button type="button" onClick={() => setFilterTab('Ready to Renew')} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Show renewals</button>
        </div>
      )}

      <div style={{ background: bankDetailsReady ? '#eff6ff' : '#fffbeb', border: `1.5px solid ${bankDetailsReady ? '#bfdbfe' : '#fde68a'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: bankDetailsReady ? '#1e40af' : '#92400e' }}>
          {bankDetailsReady ? 'Bank details active in rent emails' : 'Add bank details in Settings for rent reminder emails'}
        </span>
        <Link to="/business-settings" style={{ background: bankDetailsReady ? '#2563eb' : '#d97706', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 12, textDecoration: 'none' }}>Settings</Link>
      </div>

      <div className="ls-toolbar">
        <div className="ls-search">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenant, property, room…" />
        </div>
        <select className="ls-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
          {LEASE_FILTERS.map(f => (
            <option key={f} value={f}>{f}{filterCount(f) > 0 && f !== 'All' ? ` (${filterCount(f)})` : ''}</option>
          ))}
        </select>
      </div>

      {loading ? <p style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</p> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 12, border: '2px dashed #e5e7eb' }}>
          <div style={{ fontWeight: 700, color: '#374151' }}>No matching tenancies</div>
          <button type="button" onClick={() => navigate('/landlord/leases/new')} style={{ marginTop: 12, padding: '10px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>+ New Tenancy</button>
        </div>
      ) : (
        <>
          <div className="ls-grid">
            {pageItems.map((l: any) => {
              const badge = expiryBadge(l);
              const dep = depositBadge(l);
              const ended = isLeaseEnded(l);
              return (
                <div key={l.id} className="ls-card" onClick={() => navigate(`/landlord/leases/${l.id}`)}>
                  <div className="ls-accent" style={{ background: leaseAccent(l) }} />
                  <div className="ls-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{l.tenant_name}</div>
                      <span style={{ background: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{badge.label}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{l.property_address}</div>
                    {l.legal_landlord && (
                      <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, color: UK.blue }}>Landlord:</span> {l.legal_landlord}
                      </div>
                    )}
                    {l.room_number && <div style={{ fontSize: 12, color: '#0d9488', fontWeight: 600, marginBottom: 6 }}>Room {l.room_number}</div>}
                    <div style={{ display: 'flex', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                      <span><strong style={{ color: UK.blue }}>£{Number(l.monthly_rent).toLocaleString()}</strong>/mo</span>
                      <span style={{ color: '#94a3b8' }}>{fmt(l.start_date)} → {fmt(l.end_date)}</span>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{l.tenancy_type || 'AST'}</span>
                      {dep && <span style={{ fontSize: 11, background: dep.bg, color: dep.color, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{dep.label}</span>}
                      {(l.document_count > 0 || l.document_url) && <span style={{ fontSize: 11, background: '#eff6ff', color: UK.blue, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{l.document_count || 1} doc{(l.document_count || 1) !== 1 ? 's' : ''}</span>}
                      {readyToRenew(l) && <span style={{ fontSize: 11, background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>Renew</span>}
                    </div>
                  </div>
                  <div className="ls-card-foot" onClick={e => e.stopPropagation()}>
                    <button type="button" className="ls-act ls-act-blue" onClick={() => navigate(`/landlord/leases/${l.id}`)}>View</button>
                    <button type="button" className="ls-act ls-act-white" onClick={() => navigate(`/landlord/leases/${l.id}/edit`)}>Edit</button>
                    <button type="button" className="ls-act ls-act-white" onClick={() => generatePdf(l)} disabled={generatingId === l.id}>{generatingId === l.id ? '…' : '📄 PDF'}</button>
                    <button type="button" className="ls-act ls-act-green" onClick={() => emailAgreement(l)} disabled={emailingId === l.id}>{emailingId === l.id ? '…' : '📧 Send'}</button>
                    {!ended ? (
                      <>
                        <button type="button" className="ls-act ls-act-gold" onClick={() => openS13(l)}>S13</button>
                        <button type="button" className="ls-act ls-act-red" onClick={() => openEnd(l)}>End</button>
                      </>
                    ) : readyToRenew(l) ? (
                      <button type="button" className="ls-act ls-act-gold" style={{ gridColumn: 'span 2' }} onClick={() => navigate(`/landlord/leases/new?renew_from=${l.id}`)}>Renew</button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="ls-pager">
              <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {s13Lease && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }} onClick={() => setS13LeaseId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 560, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>Section 13 — {s13Lease.tenant_name}</h2>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>
              <strong>Draft</strong> → save. <strong>Serve notice</strong> → email tenant. <strong>Apply</strong> → updates rent on effective date.
            </p>
            <div style={{ fontSize: 13, marginBottom: 12 }}>Current: <strong>£{Number(s13Lease.monthly_rent).toLocaleString()}/mo</strong></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Proposed (£)</label><input type="number" value={s13Form.proposed_rent} onChange={e => setS13Form(f => ({ ...f, proposed_rent: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Effective</label><input type="date" value={s13Form.effective_date} onChange={e => setS13Form(f => ({ ...f, effective_date: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
            </div>
            <button type="button" onClick={() => saveS13(s13Lease)} disabled={savingS13} style={{ padding: '9px 18px', background: UK.gold, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>{savingS13 ? 'Saving…' : 'Save Draft'}</button>
            {rentIncreases.map((inc: any) => {
              const st = s13StatusLabel(inc);
              return (
                <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>£{Number(inc.proposed_rent).toLocaleString()} from {fmt(inc.effective_date)}</span>
                  <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
                  {!inc.notice_served && <button type="button" onClick={() => serveNotice(inc.id, s13Lease)} disabled={servingId === inc.id} style={{ marginLeft: 'auto', padding: '5px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Serve</button>}
                  {inc.notice_served && inc.status !== 'effective' && <button type="button" onClick={() => applyIncrease(inc.id, s13Lease)} disabled={applyingId === inc.id} style={{ marginLeft: 'auto', padding: '5px 12px', background: UK.blue, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Apply</button>}
                </div>
              );
            })}
            <button type="button" onClick={() => setS13LeaseId(null)} style={{ marginTop: 8, padding: '8px 16px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {endLease && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }} onClick={() => setEndLeaseId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 520, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800, color: UK.red }}>End Tenancy — {endLease.tenant_name}</h2>
            <div style={{ display: 'grid', gap: 12 }}>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Move-out date *</label><input type="date" value={endForm.move_out_date} onChange={e => setEndForm(f => ({ ...f, move_out_date: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Reason</label>
                <select value={endForm.reason} onChange={e => setEndForm(f => ({ ...f, reason: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }}>
                  <option value="">— Select —</option>
                  <option value="natural_expiry">Natural expiry</option>
                  <option value="early_tenant">Early — tenant</option>
                  <option value="early_landlord">Early — landlord</option>
                  <option value="mutual">Mutual agreement</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Deposit return (£)</label><input type="number" value={endForm.deposit_returned} onChange={e => setEndForm(f => ({ ...f, deposit_returned: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => confirmEnd(endLease)} disabled={ending || !endForm.move_out_date} style={{ flex: 1, padding: 10, background: UK.red, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{ending ? 'Ending…' : 'Confirm End'}</button>
              <button type="button" onClick={() => setEndLeaseId(null)} style={{ padding: '10px 16px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
